<template>
  <div class="px-3 grow bg-neutral-primary w-full rounded-b-sm">
    <div class="py-2">
      <div class="flex justify-center items-center">
        <div class="w-full h-7 relative" dir="ltr">
          <div
            class="bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 bottom-0 rounded-r-sm absolute h-1"
            :style="{
              width: `${individualFirmVolumePercentages.buyIndividual * 100}%`,
            }"
          />

          <div
            class="flex items-center h-full relative pl-1 text-brand-primary"
          >
            <Number #="{ number }" :value="symbol.buyIndividualCount">
              <NText variant="overline" weight="medium" class="mr-1">
                {{ number }} *
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="symbol.buyIndividualVolume"
              maximum-fraction-digits="2"
              minimum-fraction-digits="2"
              notation="compact"
              round
            >
              <NText variant="overline" weight="medium" class="mr-0.5">
                {{ number }}
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="individualFirmVolumePercentages.buyIndividual"
              number-style="percent"
            >
              <NText variant="overline" weight="medium"> ({{ number }}) </NText>
            </Number>
          </div>
        </div>

        <NText
          variant="overline"
          weight="bold"
          class="mx-2 text-neutral-secondary-hover"
        >
          {{ t('symbol.individual') }}
        </NText>

        <div class="w-full relative h-7">
          <div
            class="bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 bottom-0 rounded-l-sm absolute h-1"
            :style="{
              width: `${individualFirmVolumePercentages.sellIndividual * 100}%`,
            }"
          />

          <div
            class="flex items-center h-full relative pr-1 float-right text-critical-primary"
            dir="ltr"
          >
            <Number #="{ number }" :value="symbol.selIndividualCount">
              <NText variant="overline" weight="medium" class="mr-1">
                {{ number }} *
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="symbol.selIndividualVolume"
              maximum-fraction-digits="2"
              minimum-fraction-digits="2"
              notation="compact"
              round
            >
              <NText variant="overline" weight="medium" class="mr-0.5">
                {{ number }}
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="individualFirmVolumePercentages.sellIndividual"
              number-style="percent"
            >
              <NText variant="overline" weight="medium"> ({{ number }}) </NText>
            </Number>
          </div>
        </div>
      </div>
    </div>

    <div class="pb-2">
      <div class="flex justify-center items-center">
        <div class="w-full relative h-7" dir="ltr">
          <div
            class="absolute bottom-0 h-1 bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-r-sm"
            :style="{
              width: `${individualFirmVolumePercentages.buyFirm * 100}%`,
            }"
          />

          <div
            class="flex items-center h-full relative pl-1 text-brand-primary"
          >
            <Number #="{ number }" :value="symbol.buyFirmCount">
              <NText variant="overline" weight="medium" class="mr-1">
                {{ number }} *
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="symbol.buyFirmVolume"
              maximum-fraction-digits="2"
              minimum-fraction-digits="2"
              notation="compact"
              round
            >
              <NText variant="overline" weight="medium" class="mr-0.5">
                {{ number }}
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="individualFirmVolumePercentages.buyFirm"
              number-style="percent"
            >
              <NText variant="overline" weight="medium"> ({{ number }}) </NText>
            </Number>
          </div>
        </div>

        <NText
          variant="overline"
          weight="bold"
          class="mx-2 text-neutral-secondary-hover"
        >
          {{ t('symbol.firm') }}
        </NText>

        <div class="w-full relative h-7">
          <div
            class="absolute bottom-0 h-1 bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-l-sm"
            :style="{
              width: `${individualFirmVolumePercentages.sellFirm * 100}%`,
            }"
          />

          <div
            class="flex items-center h-full relative pr-1 float-right text-critical-primary"
            dir="ltr"
          >
            <Number #="{ number }" :value="symbol.selFirmCount">
              <NText variant="overline" weight="medium" class="mr-1">
                {{ number }} *
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="symbol.selFirmVolume"
              maximum-fraction-digits="2"
              minimum-fraction-digits="2"
              notation="compact"
              round
            >
              <NText variant="overline" weight="medium" class="mr-0.5">
                {{ number }}
              </NText>
            </Number>

            <Number
              #="{ number }"
              :value="individualFirmVolumePercentages.sellFirm"
              number-style="percent"
            >
              <NText variant="overline" weight="medium"> ({{ number }}) </NText>
            </Number>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

import Number from '@/components/Number.vue';
import { Instrument } from '@libs/api-reference';

const { t } = useI18n();

const props = defineProps<{ symbol: Instrument; size: string }>();

const individualFirmVolumePercentages = computed(() => {
  const {
    buyIndividualVolume,
    buyFirmVolume,
    selIndividualVolume,
    selFirmVolume,
  } = props.symbol;

  const buyIndividual = buyIndividualVolume
    ? buyIndividualVolume / (buyIndividualVolume + (buyFirmVolume ?? 0))
    : 0;
  const buyFirm = buyFirmVolume
    ? buyFirmVolume / (buyFirmVolume + (buyIndividualVolume ?? 0))
    : 0;
  const sellIndividual = selIndividualVolume
    ? selIndividualVolume / (selIndividualVolume + (selFirmVolume ?? 0))
    : 0;
  const sellFirm = selFirmVolume
    ? selFirmVolume / (selFirmVolume + (selIndividualVolume ?? 0))
    : 0;

  return {
    buyIndividual,
    buyFirm,
    sellIndividual,
    sellFirm,
  };
});
</script>
